/* =========================================================
   HGW Delivery (popup.delivery.js) - v1.3 refactor
   - Calculation only on Step2 address confirmed
   - Step3/4 are render-only
========================================================= */

window.HGW = window.HGW || {};

/* ======================================
   Helpers
====================================== */

function hgwBuildDeliveryCalcKey(d) {
  const a = (d.address || "").trim().toLowerCase();
  const s = (d.suburb || "").trim().toLowerCase();
  const p = (d.postcode || "").trim();
  const lat = d.lat != null ? Number(d.lat).toFixed(6) : "";
  const lng = d.lng != null ? Number(d.lng).toFixed(6) : "";
  return `${a}|${s}|${p}|${lat}|${lng}`;
}

function hgwMoney(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return `$${num.toFixed(2)}`;
}

function hgwGetShippingVariantId(d) {
  const parcel = Number(d?.parcel_count || 0);

  if (!parcel) return null;

  const variantId = HGW_CONFIG.shippingFeeVariants.aramex_metro.no_signature[parcel];

  return variantId ? Number(variantId) : null;
}

/* ======================================
   Google Places Loader
   - safely loads Maps JS once
   - retries init when Step2 DOM appears
====================================== */

function loadGoogleMapsPlaces() {
  if (window.google?.maps?.places) {
    window.initGoogleAutocomplete?.();
    return;
  }

  if (window.__hgwGoogleLoading) return;
  window.__hgwGoogleLoading = true;

  const apiKey =
    window.HGW_GOOGLE_MAPS_API_KEY ||
    window.HGW_GOOGLE_API_KEY ||
    "";

  const s = document.createElement("script");
  s.async = true;
  s.defer = true;

  s.src =
    "https://maps.googleapis.com/maps/api/js" +
    `?key=${encodeURIComponent(apiKey)}` +
    "&libraries=places&callback=initGoogleAutocomplete&language=en&region=AU";

  document.head.appendChild(s);
}

window.HGW_loadGoogleMapsPlaces = function () {
  loadGoogleMapsPlaces();

  let tries = 0;
  const maxTries = 60; // ~1초

  const tick = () => {
    tries++;
    const input = document.getElementById("delivery-address");

    if (input && window.google?.maps?.places) {
      window.initGoogleAutocomplete?.();
      return;
    }

    if (tries < maxTries) requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
};

/* ======================================
   Google Autocomplete Init (global callback)
====================================== */

function initGoogleAutocomplete() {
  const input = document.getElementById("delivery-address");
  const suburbInput = document.getElementById("delivery-suburb");
  const postcodeInput = document.getElementById("delivery-postcode");

  if (!input) return;
  if (input.dataset.googleBound === "1") return;

  let autocomplete;
  try {
    autocomplete = new google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: "au" },
      fields: ["address_components", "geometry"],
      types: ["address"]
    });
  } catch (err) {
    console.error("[HGW] Google Autocomplete init failed:", err);
    return;
  }

  input.dataset.googleBound = "1";

  autocomplete.addListener("place_changed", async () => {
    const place = autocomplete.getPlace();
    if (!place?.geometry?.location) return;

    let streetNumber = "";
    let route = "";
    let suburb = "";
    let postcode = "";

    (place.address_components || []).forEach((component) => {
      const types = component.types || [];
      if (types.includes("street_number")) streetNumber = component.long_name;
      if (types.includes("route")) route = component.long_name;
      if (types.includes("locality")) suburb = component.long_name;
      if (types.includes("postal_code")) postcode = component.long_name;
    });

    const street = [streetNumber, route].filter(Boolean).join(" ").trim();
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    if (suburbInput) suburbInput.value = suburb;
    if (postcodeInput) postcodeInput.value = postcode;

    const prevDelivery = HGW.state.get()?.delivery || {};
    const newKey = `${street}|${suburb}|${postcode}|${lat}|${lng}`;
    const oldKey = hgwBuildDeliveryCalcKey(prevDelivery);

    const addressChanged = newKey !== oldKey;

    const nextDelivery = {
      ...prevDelivery,
      address: street || input.value,
      suburb,
      postcode,
      lat,
      lng,
      ...(addressChanged
        ? {
            calc_key: "",
            calc_status: "idle",
            calc_error: ""
          }
        : {})
    };

    HGW.state.update({
      address: nextDelivery.address,
      suburb: nextDelivery.suburb,
      postcode: nextDelivery.postcode,
      delivery: nextDelivery
    });

    HGW.delivery?.resolveDeliveryFromState?.({ force: false });

    requestAnimationFrame(() => {
      HGW.core?.validate?.();
    });
  });
}

window.initGoogleAutocomplete = initGoogleAutocomplete;

/* ======================================
   DELIVERY POLICY NORMALIZE
====================================== */

HGW.deliveryPolicy = (function(raw){

  if (!raw) return null;

  const parseJSON = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    try { return JSON.parse(v); } catch { return []; }
  };

  return {
    dispatch_enabled_days: parseJSON(raw.dispatch_enabled_days),
    movement_days: parseJSON(raw.movement_days),
    zone_rules: typeof raw.zone_rules === "string"
      ? JSON.parse(raw.zone_rules)
      : (raw.zone_rules || {}),

    max_select_days: Number(raw.max_select_days || 7),
    movement_processing_buffer_days: Number(raw.movement_processing_buffer_days || 1),

    metro_eta_days: Number(raw.metro_eta_days || 1),
    regional_eta_days: Number(raw.regional_eta_days || 2)
  };

})(window.HGW_DELIVERY_POLICY);

/* ======================================
   DELIVERY ENGINE
====================================== */

HGW.delivery = {
  /* --------------------------------------
     Resolve Delivery via Backend (calculation)
     - called only when address confirmed
     - cached by calc_key + calc_status
  -------------------------------------- */

  async finalizeDeliveryForStep5() {
    const s = HGW.state.get();
    const d = s.delivery || {};

    if (s.method !== "delivery") {
      return { ok: false, reason: "not_delivery" };
    }

    if (d.calc_status !== "ready") {
      return { ok: false, reason: "calc_not_ready" };
    }

    if (!d.dispatch_date) {
      return { ok: false, reason: "dispatch_missing" };
    }

    const variantId = hgwGetShippingVariantId(d);
    if (!variantId) {
      return { ok: false, reason: "variant_missing" };
    }

    try {
      const cart = await fetch("/cart.js", { cache: "no-store" }).then(r => r.json());
      const cartSig = HGW.shippingCart.buildCartSignature(cart);

      // Step2에서 stale 제거를 했더라도, 안전하게 한 번 더 정리
      await HGW.shippingCart.removeShippingFee(cart);

      const addRes = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _hgw_shipping: "true",
                _hgw_cart_sig: cartSig,
                provider: String(d.provider || ""),
                parcel_count: String(d.parcel_count || ""),
                signature: "No",
                dispatch_date: String(d.dispatch_date || "")
              }
            }
          ]
        })
      });

      if (!addRes.ok) {
        throw new Error(`cart/add.js failed: ${addRes.status}`);
      }

      const current = HGW.state.get().delivery || {};

      HGW.state.update({
        delivery: {
          ...current,
          shipping_variant_id: String(variantId),
          shipping_cart_sig: String(cartSig),
          finalized: true
        }
      });

      return { ok: true, variantId, cartSig };

    } catch (err) {
      console.error("[HGW] finalizeDeliveryForStep5 error", err);
      return { ok: false, error: err };
    }
  },

  async resolveDeliveryFromState({ force = false } = {}) {
    const s = HGW.state.get();
    const d = s.delivery || {};

    const fullAddress = [d.address, d.suburb, d.postcode]
      .filter(Boolean)
      .join(", ")
      .trim();

    if (!fullAddress) return { ok: false, reason: "address_missing" };

    const key = hgwBuildDeliveryCalcKey(d);

    /* ======================================
      Skip cache ONLY if address unchanged
      AND not forced
    ====================================== */

    if (!force && d.calc_key === key && d.calc_status === "ready") {

      console.log("[HGW] Delivery calc skipped (cached)");

      return { ok: true, cached: true };

    }

    if (d.calc_status === "loading") {
      return { ok: false, reason: "already_loading" };
    }

    // 🔥 loading 상태 반영
    HGW.state.update({
      delivery: {
        ...d,
        calc_key: key,
        calc_status: "loading",
        calc_error: ""
      }
    });

    // 🔥 즉시 재렌더 (loading UI 반영)
    const currentStepLoading = HGW.state.get().step;
    if (currentStepLoading === 3) HGW.delivery.renderStep3();
    if (currentStepLoading === 4) HGW.delivery.renderStep4();

    try {
      const cart = await fetch("/cart.js").then((r) => r.json());

      const items = (cart.items || []).map((item) => ({
        variant_id: String(item.variant_id),
        quantity: Number(item.quantity)
      }));

      if (!items.length) {
        throw new Error("cart_empty");
      }

      const endpoint = window.HGW_SHIPPING_ENDPOINT;
      if (!endpoint) throw new Error("HGW_SHIPPING_ENDPOINT not defined");

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items,
          address: fullAddress,
          lat: d.lat || null,
          lng: d.lng || null
        })
      });

      const data = await res.json();

      if (!res.ok || !data?.delivery) {
        throw new Error("delivery_failed");
      }

      const shippingCost = Number(data.delivery?.standard?.shipping_cost || 0);
      const parcelCount = Number(data.packing_summary?.parcelCount || 0);
      const isMetro = !!data.delivery?.metro;

      const current = HGW.state.get().delivery || {};

      HGW.state.update({
        delivery: {
          ...current,

          is_metro: isMetro,
          area_label: isMetro ? "Perth Metro" : "WA Regional",

          parcel_count: parcelCount,
          base_shipping_cost: shippingCost,

          provider: "ARAMEX",
          store_name: "Aramex Standard",

          lat: data.customer_coords?.lat ?? current.lat,
          lng: data.customer_coords?.lng ?? current.lng,

          calc_key: key,
          calc_status: "ready",
          calc_error: ""
        }
      });

      return { ok: true, response: data };

    } catch (err) {

      const current = HGW.state.get().delivery || {};

      HGW.state.update({
        delivery: {
          ...current,
          calc_status: "error",
          calc_error: err?.message || "unknown_error"
        }
      });

      return { ok: false, error: err };

    } finally {

      // 🔥 핵심: 상태 변경 후 항상 현재 step 재렌더
      requestAnimationFrame(() => {
        const step = HGW.state.get().step;

        if (step === 3) HGW.delivery.renderStep3();
        if (step === 4) HGW.delivery.renderStep4();

        HGW.core?.renderDeliverySummary?.();
        HGW.core?.validate?.();
      });
    }
  },

  /* --------------------------------------
     STEP 3 - Policy Display (render-only)
  -------------------------------------- */

  renderStep3() {
    const d = HGW.state.get().delivery || {};

    const parcelEl = document.getElementById("hgw-parcel-count");
    const costEl = document.getElementById("hgw-shipping-cost");
    const areaEl = document.getElementById("hgw-delivery-area");
    const warningEl = document.getElementById("hgw-policy-warning");
    const checkbox = document.getElementById("hgw-policy-agree");

    if (d.calc_status === "loading") {
      if (parcelEl) parcelEl.textContent = "—";
      if (costEl) costEl.textContent = "Calculating...";
      if (areaEl) areaEl.textContent = "—";
      if (warningEl) warningEl.innerHTML = "";
      if (checkbox) checkbox.checked = !!d.policy_agreed;
      return;
    }

    if (d.calc_status === "error") {
      if (parcelEl) parcelEl.textContent = "—";
      if (costEl) costEl.textContent = "—";
      if (areaEl) areaEl.textContent = "—";
      if (warningEl) {
        warningEl.innerHTML = `
          <div class="hgw-warning">
            ⚠ Unable to calculate shipping. Please reselect your address.
          </div>
        `;
      }
      if (checkbox) checkbox.checked = !!d.policy_agreed;
      return;
    }

    if (parcelEl) parcelEl.textContent = d.parcel_count ?? 0;
    if (costEl) {
      const base = 9.99;
      const extra = Math.max(0, (d.parcel_count || 1) - 1) * 5;

      if (extra > 0) {
        costEl.innerHTML =
          `$${base.toFixed(2)} + <span style="text-decoration:line-through;color:#999;">$${extra.toFixed(2)}</span>`;
      } else {
        costEl.textContent = `$${base.toFixed(2)}`;
      }
    }
    if (areaEl) areaEl.textContent = d.area_label || (d.is_metro ? "Perth Metro" : "WA Regional");

    if (warningEl) {
      warningEl.innerHTML = d.is_metro
        ? ""
        : `
          <div class="hgw-warning">
            ⚠ Additional shipping cost may apply.<br>
            ⚠ Delivery may take longer than 2 business days.
          </div>
        `;
    }

    if (checkbox) {
      checkbox.checked = !!d.policy_agreed;
      checkbox.onchange = function () {
        const cur = HGW.state.get().delivery || {};

        HGW.state.update({
          delivery: {
            ...cur,
            policy_agreed: this.checked
          }
        });

        HGW.core?.validate?.();
      };
    }
  },

  /* --------------------------------------
     STEP 4 - Dispatch Date + Signature (render-only)
  -------------------------------------- */

  renderStep4() {

    const wrapper = document.getElementById("hgw-calendar-container");
    if (!wrapper || !HGW.calendar?.open) return;

    wrapper.innerHTML = `
      <div id="hgw-calendar-mount"></div>
      <div id="hgw-calendar-options"></div>
    `;

    const calendarMount = wrapper.querySelector("#hgw-calendar-mount");
    const optionsMount = wrapper.querySelector("#hgw-calendar-options");

    /* ======================================
      POLICY 기반 최소 날짜 계산
    ====================================== */

    const policy = HGW.deliveryPolicy;
    const movementRequired = true;

    const startISO = HGW.delivery.getEarliestDispatchISO({
      movementRequired
    });

    HGW.calendar.open({
      calendar: wrapper,
      mount: calendarMount,
      mode: "delivery",
      minDate: startISO,
      maxDays: policy?.max_select_days || 7
    });

    /* ======================================
      ETA 안내 카드 (정책 기반)
    ====================================== */

    const infoBox = document.getElementById("hgw-dispatch-info");
    if (infoBox) {

      const d = HGW.state.get().delivery || {};
      const zone = d.is_metro ? "metro" : "regional";

      const etaDays =
        policy?.zone_rules?.[zone]?.eta
        ?? (zone === "metro"
              ? policy?.metro_eta_days
              : policy?.regional_eta_days);

      infoBox.innerHTML = `
        <div class="hgw-dispatch-card ${zone}">
          <strong>${zone === "metro" ? "Metro Delivery" : "WA Regional Delivery"}</strong><br>
          Estimated delivery: <strong>1~2 business days</strong><br>
          after dispatch.
        </div>
      `;
    }

    /* ======================================
      Signature Option
      - temporarily disabled
    ====================================== */

    const d0 = HGW.state.get().delivery || {};

    HGW.state.update({
      delivery: {
        ...d0,
        signature_required: false,
        surcharges: {
          ...(d0.surcharges || {}),
          signature: 0
        },
        shipping_variant_id: "",
        shipping_cart_sig: "",
        finalized: false
      }
    });

    optionsMount.innerHTML = "";

    requestAnimationFrame(() => {
      HGW.core?.validate?.();
    });
  },

  /* --------------------------------------
     Earliest Dispatch (Movement + Dispatch Days)
  -------------------------------------- */
  getEarliestDispatchISO({ movementRequired = true } = {}) {

    const policy = HGW.deliveryPolicy;
    if (!policy) return null;

    const today = new Date();
    const weekdayMap = ["sun","mon","tue","wed","thu","fri","sat"];

    const movementDays = policy.movement_days;
    const dispatchDays = policy.dispatch_enabled_days;

    if (!dispatchDays?.length) return null;

    /* =====================================
      🚚 CASE 1 — Movement 필요 없음
    ===================================== */

    if (!movementRequired) {

      // 오늘 제외하고 가장 빠른 dispatch day 찾기
      for (let i = 1; i <= 14; i++) {
        const d = HGW.utils.addDays(today, i);
        const w = weekdayMap[d.getDay()];

        if (dispatchDays.includes(w)) {
          return HGW.utils.formatISO(d);
        }
      }

      return null;
    }

    /* =====================================
      🚚 CASE 2 — Movement 필요
    ===================================== */

    if (!movementDays?.length) return null;

    // 1️⃣ 첫 movement (오늘 제외)
    let firstMovement = null;

    for (let i = 1; i <= 14; i++) {
      const d = HGW.utils.addDays(today, i);
      const w = weekdayMap[d.getDay()];

      if (movementDays.includes(w)) {
        firstMovement = new Date(d);
        break;
      }
    }

    if (!firstMovement) return null;

    // 2️⃣ 두 번째 movement
    let secondMovement = null;

    for (let i = 1; i <= 14; i++) {
      const d = HGW.utils.addDays(firstMovement, i);
      const w = weekdayMap[d.getDay()];

      if (movementDays.includes(w)) {
        secondMovement = new Date(d);
        break;
      }
    }

    if (!secondMovement) return null;

    // 3️⃣ 다음날 dispatch 가능
    const readyDate = HGW.utils.addDays(secondMovement, 1);

    for (let i = 0; i <= 14; i++) {
      const d = HGW.utils.addDays(readyDate, i);
      const w = weekdayMap[d.getDay()];

      if (dispatchDays.includes(w)) {
        return HGW.utils.formatISO(d);
      }
    }

    return null;
  }
};